import { CSVFile, ValidationResult, ValidationError, DetectedFormat } from '../types/data/csv';
const CSVSniffer = require('csv-sniffer');

class CSVService {
  /**
   * Parse a CSV file using csv-sniffer for auto-detection
   */
  async parseCSV(file: File): Promise<CSVFile> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          if (!content) {
            throw new Error('Failed to read file');
          }
          
          if (content.trim().length === 0) {
            throw new Error('The file is empty');
          }
          
          // Ensure content ends with a newline to prevent csv-sniffer from dropping the last row
          const normalizedContent = content.endsWith('\n') ? content : content + '\n';
          
          // Use csv-sniffer for auto-detection and parsing
          const CSVSnifferClass = CSVSniffer();
          const sniffer = new CSVSnifferClass([',', ';', '\t', '|']);
          const result = sniffer.sniff(normalizedContent, {
            hasHeader: true
          });
          
          if (!result.delimiter) {
            throw new Error('Could not detect CSV format. Please ensure the file is a valid CSV with comma, semicolon, tab, or pipe delimiters.');
          }
          
          console.log('🔍 CSV Format Detected:', {
            delimiter: result.delimiter,
            quoteChar: result.quoteChar,
            hasHeader: result.hasHeader,
            recordCount: result.records.length,
            originalContentLength: content.length,
            normalizedContentLength: normalizedContent.length,
            contentEndsWithNewline: content.endsWith('\n')
          });
          
          // Determine if this is European format
          const isEuropeanFormat = result.delimiter === ';';
          
          // Process the data - csv-sniffer already parsed it for us
          const headers = result.labels || (result.records.length > 0 ? result.records[0] : []);
          let rows = result.hasHeader && result.records.length > 0 ? result.records : result.records.slice(1);
          
          // Strip surrounding quotes from all values (csv-sniffer doesn't always do this)
          const stripQuotes = (value: string): string => {
            if (typeof value === 'string' && value.length >= 2) {
              // Check if value is wrapped in quotes (single or double)
              if ((value.startsWith('"') && value.endsWith('"')) || 
                  (value.startsWith("'") && value.endsWith("'"))) {
                return value.slice(1, -1);
              }
            }
            return value;
          };
          
          // Clean headers
          const cleanedHeaders = headers.map((h: any) => stripQuotes(h?.toString() || ''));
          
          // Clean all row values
          let cleanedRows = rows.map((row: any) => 
            row.map((cell: any) => stripQuotes(cell?.toString() || ''))
          );
          
          console.log('🧹 Cleaned quotes from CSV data');
          console.log('Original header sample:', headers[0]);
          console.log('Cleaned header sample:', cleanedHeaders[0]);
          
          // Use cleaned data from here on
          rows = cleanedRows;
          
          // Convert European numbers to UK format if needed
          if (isEuropeanFormat) {
            console.log('🌍 European format detected - converting numbers to UK format...');
            rows = this.convertCSVNumbersToUKFormat(rows, isEuropeanFormat);
          }
          
          // Create preview data (first 5 rows) - after conversion
          const previewData = rows.slice(0, 5);
          
          console.log('🔍 Final CSV Processing Results:', {
            headersCount: headers.length,
            dataRowsCount: rows.length,
            previewRowsCount: previewData.length,
            expectedDataRows: result.hasHeader ? result.records.length : result.records.length - 1
          });
          
          // Create detected format info
          const detectedFormat: DetectedFormat = {
            delimiter: result.delimiter,
            quoteChar: result.quoteChar,
            newlineStr: result.newlineStr,
            hasHeader: result.hasHeader,
            isEuropeanFormat: isEuropeanFormat,
            warnings: result.warnings
          };
          
          // Create a unique id for the file
          const fileId = `csv_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
          
          const csvFile: CSVFile = {
            id: fileId,
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
            uploadDate: Date.now(),
            columns: cleanedHeaders,
            rows: rows,
            previewData: previewData,
            fileType: 'csv',
            detectedFormat: detectedFormat
          };
          
          resolve(csvFile);
        } catch (error) {
          console.error('CSV parsing error:', error);
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };
      
      reader.onerror = (error) => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  }
  
  /**
   * Helper method to detect if a CSV uses European number format
   */
  private detectEuropeanNumbers(rows: string[][], delimiter: string): boolean {
    if (delimiter !== ';') return false;
    
    // Sample a few rows to detect European number patterns
    const sampleRows = rows.slice(0, Math.min(10, rows.length));
    let europeanNumberCount = 0;
    let totalNumbers = 0;
    
    for (const row of sampleRows) {
      for (const cell of row) {
        // Check for European number format: 1.234,56 or 1234,56
        if (/^\d{1,3}(\.\d{3})*,\d{2}$/.test(cell.trim()) || /^\d+,\d{2}$/.test(cell.trim())) {
          europeanNumberCount++;
          totalNumbers++;
        } else if (/^\d{1,3}(,\d{3})*\.\d{2}$/.test(cell.trim()) || /^\d+\.\d{2}$/.test(cell.trim())) {
          totalNumbers++;
        }
      }
    }
    
    // If more than 50% of detected numbers use European format, assume European CSV
    return totalNumbers > 0 && (europeanNumberCount / totalNumbers) > 0.5;
  }

  /**
   * Convert European number format to UK/US format
   */
  private convertEuropeanToUKNumber(value: string): string {
    const trimmed = value.trim();
    
    // Check if it looks like a European number
    if (/^\d{1,3}(\.\d{3})*,\d{1,}$/.test(trimmed)) {
      // Format: 1.234.567,89 -> 1234567.89
      return trimmed.replace(/\./g, '').replace(',', '.');
    } else if (/^\d+,\d{1,}$/.test(trimmed) && !trimmed.includes('.')) {
      // Format: 1234,56 -> 1234.56 (only if no periods present)
      return trimmed.replace(',', '.');
    }
    
    // Return unchanged if not European format
    return trimmed;
  }

  /**
   * Convert all numbers in CSV data from European to UK format
   */
  private convertCSVNumbersToUKFormat(rows: string[][], isEuropeanFormat: boolean): string[][] {
    if (!isEuropeanFormat) {
      return rows; // No conversion needed
    }

    console.log('🔄 Converting European number format to UK format...');
    
    return rows.map((row, rowIndex) => {
      return row.map((cell, cellIndex) => {
        const converted = this.convertEuropeanToUKNumber(cell);
        
        // Log conversion examples for debugging
        if (rowIndex < 3 && converted !== cell) {
          console.log(`🔄 Converted: "${cell}" -> "${converted}"`);
        }
        
        return converted;
      });
    });
  }
  
  /**
   * Validate the CSV file for financial data
   */
  validateCSVForFinancials(csvFile: CSVFile): ValidationResult {
    const errors: ValidationError[] = [];
    
    // Define required column headers (exact match, case-sensitive)
    const requiredHeaders = [
      'Brand',
      'Dealer', 
      'Year',
      'Month',
      'KPI Code',
      'Value',
      'KPI Name',
      'KPI Category'
    ];
    
    // Check if file has headers
    if (!csvFile.columns || csvFile.columns.length === 0) {
      errors.push({
        row: 0,
        column: 'headers',
        message: 'CSV file must contain column headers'
      });
      return {
        isValid: false,
        errors: errors
      };
    }
    
    // Check for required headers
    const missingHeaders: string[] = [];
    const csvHeaders = csvFile.columns.map(header => header.trim());
    
    for (const requiredHeader of requiredHeaders) {
      if (!csvHeaders.includes(requiredHeader)) {
        missingHeaders.push(requiredHeader);
      }
    }
    
    // Add errors for missing headers
    if (missingHeaders.length > 0) {
      errors.push({
        row: 0,
        column: 'headers',
        message: `Missing required column headers: ${missingHeaders.join(', ')}. 

Your CSV file must contain ALL of these column headers (case-sensitive):
${requiredHeaders.map(header => `• ${header}`).join('\n')}

Found headers in your file:
${csvHeaders.map(header => `• ${header}`).join('\n')}

Please download the example file to see the correct format.`
      });
    }
    
    // Additional validation: check if file has data rows
    if (csvFile.rows.length === 0) {
      errors.push({
        row: 0,
        column: 'data',
        message: 'CSV file must contain data rows'
      });
    }

    // If all required headers are present, validate data format
    if (missingHeaders.length === 0 && csvFile.rows.length > 0) {
      const yearIndex = csvHeaders.indexOf('Year');
      const monthIndex = csvHeaders.indexOf('Month');
      const valueIndex = csvHeaders.indexOf('Value');
      const brandIndex = csvHeaders.indexOf('Brand');
      const dealerIndex = csvHeaders.indexOf('Dealer');
      const kpiCodeIndex = csvHeaders.indexOf('KPI Code');

      // Validate first few rows for data format
      const rowsToValidate = Math.min(csvFile.rows.length, 5);
      
      for (let i = 0; i < rowsToValidate; i++) {
        const row = csvFile.rows[i];
        const rowNumber = i + 2; // +2 because row 1 is headers and array is 0-indexed

        // Check for empty required fields
        if (!row[brandIndex] || row[brandIndex].trim() === '') {
          errors.push({
            row: rowNumber,
            column: 'Brand',
            message: `Row ${rowNumber}: Brand field cannot be empty`
          });
        }

        if (!row[dealerIndex] || row[dealerIndex].trim() === '') {
          errors.push({
            row: rowNumber,
            column: 'Dealer',
            message: `Row ${rowNumber}: Dealer field cannot be empty`
          });
        }

        if (!row[kpiCodeIndex] || row[kpiCodeIndex].trim() === '') {
          errors.push({
            row: rowNumber,
            column: 'KPI Code',
            message: `Row ${rowNumber}: KPI Code field cannot be empty`
          });
        }

        // Validate Year format (should be a 4-digit number)
        if (row[yearIndex]) {
          const year = parseInt(row[yearIndex]);
          if (isNaN(year) || year < 2000 || year > 2030) {
            errors.push({
              row: rowNumber,
              column: 'Year',
              message: `Row ${rowNumber}: Year must be a valid 4-digit number between 2000 and 2030`
            });
          }
        }

        // Validate Month format (should be 1-12)
        if (row[monthIndex]) {
          const month = parseInt(row[monthIndex]);
          if (isNaN(month) || month < 1 || month > 12) {
            errors.push({
              row: rowNumber,
              column: 'Month',
              message: `Row ${rowNumber}: Month must be a number between 1 and 12`
            });
          }
        }

        // Validate Value format (should be a number)
        if (row[valueIndex] && row[valueIndex].trim() !== '') {
          const value = parseFloat(row[valueIndex]);
          if (isNaN(value)) {
            errors.push({
              row: rowNumber,
              column: 'Value',
              message: `Row ${rowNumber}: Value must be a valid number`
            });
          }
        }
      }
    }
    
    // Log validation results for debugging
    if (errors.length > 0) {
      console.log('CSV Validation failed:', errors);
      console.log('Found headers:', csvHeaders);
      console.log('Required headers:', requiredHeaders);
    } else {
      console.log('CSV Validation passed');
      console.log('Found headers:', csvHeaders);
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
  
  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Extract KPI data from CSV
   */
  extractKPIData(csvData: string[][]): { headers: string[], kpiData: string[][] } {
    if (!csvData || csvData.length <= 1) {
      console.log('No CSV data available for KPI extraction');
      return { headers: [], kpiData: [] };
    }

    const headers = csvData[0];
    const rows = csvData.slice(1);

    // Find all columns that contain "KPI" or "Field_Code" in their header
    const kpiColumnIndices = headers.map((header, index) => {
      const headerLower = header.toString().toLowerCase();
      return headerLower.includes('kpi') || headerLower.includes('field_code') ? index : -1;
    }).filter(index => index !== -1);

    if (kpiColumnIndices.length === 0) {
      console.log('No KPI or Field_Code columns found in the data');
      return { headers: [], kpiData: [] };
    }

    // Check if "KPI Name" column exists and prioritize it
    const kpiNameIndex = headers.findIndex(header => 
      header.toString().toLowerCase() === 'kpi name');
    
    let selectedKpiIndices: number[] = [];
    
    if (kpiNameIndex !== -1) {
      // If "KPI Name" column exists, use only that column
      console.log('Found "KPI Name" column - prioritizing this column only');
      selectedKpiIndices = [kpiNameIndex];
    } else {
      // Otherwise use all KPI-related columns found earlier
      selectedKpiIndices = kpiColumnIndices;
    }

    // Get the KPI headers
    const kpiHeaders = selectedKpiIndices.map(index => headers[index]);

    // Create a map to store unique values for each KPI column
    const kpiColumnValues = new Map<number, Set<string>>();
    selectedKpiIndices.forEach(index => {
      kpiColumnValues.set(index, new Set<string>());
    });

    // Extract values for each KPI column separately
    rows.forEach(row => {
      selectedKpiIndices.forEach(index => {
        const value = row[index]?.trim();
        if (value) {
          kpiColumnValues.get(index)?.add(value);
        }
      });
    });

    // Convert the map to a 2D array format
    // Each row in kpiData will represent a unique combination of KPI values
    const kpiData: string[][] = [];
    
    // Create rows for each unique value in the first KPI column
    const firstColumnValues = Array.from(kpiColumnValues.get(selectedKpiIndices[0]) || []).sort();
    firstColumnValues.forEach(value => {
      const row = new Array(selectedKpiIndices.length).fill('');
      row[0] = value;
      kpiData.push(row);
    });

    // Fill in values for other columns
    for (let colIndex = 1; colIndex < selectedKpiIndices.length; colIndex++) {
      const columnValues = Array.from(kpiColumnValues.get(selectedKpiIndices[colIndex]) || []).sort();
      columnValues.forEach((value, valueIndex) => {
        if (valueIndex < kpiData.length) {
          kpiData[valueIndex][colIndex] = value;
        } else {
          // If we have more values than rows, create new rows
          const row = new Array(selectedKpiIndices.length).fill('');
          row[colIndex] = value;
          kpiData.push(row);
        }
      });
    }

    console.log('KPI Analysis:');
    console.log(`- Found ${selectedKpiIndices.length} KPI columns (${kpiNameIndex !== -1 ? 'using only KPI Name column' : 'using all KPI-related columns'})`);
    console.log(`- Found ${kpiData.length} rows of KPI data`);
    console.log('KPI Headers:', kpiHeaders);
    console.log('KPI Data sample:', kpiData.slice(0, 3));

    return { headers: kpiHeaders, kpiData };
  }
}

export default new CSVService(); 